// Remote support — the HELPER's viewer (shared/REMOTE-SUPPORT.md §6), plain DOM so the same code draws in
// the web client (mounted by ui/features/remoteSupport/) and in the Windows supporthost (src/supporthost/,
// a WebView2 window of its own). It draws:
//   toolbar: status («Conectando…» / «Sólo ver» / «Control activo»), the requester's screen name, the
//            release-combo hint, «Pedir control» (view-only), «Pantalla completa», «Terminar»;
//   stage:   the remote video letterboxed (object-fit: contain) — pointer, wheel and keyboard over it go
//            through the session's InputEncoder (which sends nothing unless control is granted).
// Keyboard: while the stage has focus every key goes to the remote machine (preventDefault), except the
// helper's own release combo Ctrl+Alt+Shift+R, which stops key forwarding until the stage is clicked again.
// In full screen the Keyboard Lock API (where the browser has it) also captures Esc / Alt+Tab-class keys.
import './viewer.css';
import { letterbox, toNormalised } from './geometry';
import { modifiers } from './protocol';
import type { HelperSession, HelperViewState } from './session';

export type ViewerText = (key: string, ...args: Array<string | number>) => string;

export interface ViewerOptions {
  root: HTMLElement;
  session: HelperSession;
  /** The requester's display name. */
  peerName: string;
  text: ViewerText;
  /** «Terminar» pressed. The owner posts `/end` and disposes the viewer. */
  onEnd(): void;
  /** Debug/test hook: every message the stage produced (normalised), before the encoder's gate. */
  onTrace?(line: string): void;
}

const ELEVATED_NOTICE_MS = 6_000;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class SupportViewer {
  private readonly wrap: HTMLDivElement;
  private readonly status: HTMLSpanElement;
  private readonly title: HTMLSpanElement;
  private readonly hint: HTMLSpanElement;
  private readonly askButton: HTMLButtonElement;
  private readonly fullButton: HTMLButtonElement;
  private readonly endButton: HTMLButtonElement;
  private readonly stage: HTMLDivElement;
  private readonly video: HTMLVideoElement;
  private readonly waiting: HTMLDivElement;
  private readonly toast: HTMLDivElement;
  private state: HelperViewState;
  private stream: MediaStream | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly cleanups: Array<() => void> = [];

  constructor(private readonly o: ViewerOptions) {
    const { text } = o;
    this.state = o.session.state;
    this.wrap = el('div', 'rsv');
    this.wrap.setAttribute('data-testid', 'rs-viewer');
    const bar = el('div', 'rsv-bar');
    this.status = el('span', 'rsv-status');
    this.status.setAttribute('data-testid', 'rs-status');
    this.title = el('span', 'rsv-title', text('web.rs.viewer.title', o.peerName));
    this.hint = el('span', 'rsv-hint', text('web.rs.releaseHint'));
    const spacer = el('span', 'rsv-spacer');
    this.askButton = el('button', 'rsv-button', text('web.rs.askControl'));
    this.askButton.type = 'button';
    this.askButton.setAttribute('data-testid', 'rs-ask');
    this.fullButton = el('button', 'rsv-button', text('web.rs.fullscreen'));
    this.fullButton.type = 'button';
    this.endButton = el('button', 'rsv-button rsv-end', text('web.rs.end'));
    this.endButton.type = 'button';
    this.endButton.setAttribute('data-testid', 'rs-end');
    bar.append(this.status, this.title, this.hint, spacer, this.askButton, this.fullButton, this.endButton);

    this.stage = el('div', 'rsv-stage');
    this.stage.tabIndex = 0;
    this.stage.setAttribute('data-testid', 'rs-stage');
    this.stage.setAttribute('aria-label', text('web.rs.viewer.title', o.peerName));
    this.video = el('video', 'rsv-video');
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.waiting = el('div', 'rsv-waiting', text('web.rs.viewer.waiting', o.peerName));
    this.toast = el('div', 'rsv-toast');
    this.toast.setAttribute('role', 'status');
    this.toast.hidden = true;
    this.stage.append(this.video, this.waiting, this.toast);
    this.wrap.append(bar, this.stage);
    o.root.append(this.wrap);

    this.askButton.addEventListener('click', () => o.session.askForControl());
    this.endButton.addEventListener('click', () => o.onEnd());
    this.fullButton.addEventListener('click', () => void this.toggleFullscreen());
    this.listen(document, 'fullscreenchange', () => this.onFullscreenChange());
    this.bindInput();
    this.render(this.state);
  }

  /** Draw a new session state (the owner forwards every HelperSession update). */
  render(state: HelperViewState): void {
    const prev = this.state;
    this.state = state;
    const { text } = this.o;
    const reconnecting = state.phase === 'connected' && (state.link === 'disconnected' || state.link === 'failed');
    const label =
      state.phase !== 'connected' ? text('web.rs.status.connecting') : reconnecting ? text('web.rs.status.reconnecting') : state.control ? text('web.rs.status.control') : text('web.rs.status.viewOnly');
    this.status.textContent = label;
    this.status.dataset.tone = state.phase !== 'connected' || reconnecting ? 'wait' : state.control ? 'control' : 'view';
    this.wrap.dataset.control = state.control ? 'on' : 'off';
    if (state.remote?.name) this.title.textContent = `${text('web.rs.viewer.title', this.o.peerName)} · ${state.remote.name}`;
    this.askButton.hidden = state.control || state.phase !== 'connected';
    this.askButton.disabled = state.asked || state.noControl;
    this.askButton.textContent = state.asked ? text('web.rs.asked') : text('web.rs.askControl');
    this.hint.hidden = !state.control;
    if (state.stream !== this.stream) {
      this.stream = state.stream;
      this.video.srcObject = state.stream;
      if (state.stream) void this.video.play().catch(() => undefined);
    }
    this.waiting.hidden = !!state.stream;
    if (state.keysPaused && !prev.keysPaused) this.showToast(text('web.rs.keysPaused'), 0);
    else if (!state.keysPaused && prev.keysPaused) this.hideToast();
    if (state.notice === 'elevated' && state.noticeAt !== prev.noticeAt) this.showToast(text('web.rs.elevated'), ELEVATED_NOTICE_MS);
    // A Mac without Accessibility can only be watched: said once, and it stays (0 = no timeout).
    if (state.noControl && !prev.noControl) this.showToast(text('web.rs.noPermission'), 0);
    this.stage.classList.toggle('capturing', state.control);
  }

  focus(): void {
    this.stage.focus();
  }

  dispose(): void {
    for (const c of this.cleanups.splice(0)) c();
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (document.fullscreenElement === this.wrap) void document.exitFullscreen().catch(() => undefined);
    this.video.srcObject = null;
    this.wrap.remove();
  }

  private listen<T extends Event>(target: EventTarget, type: string, fn: (e: T) => void, options?: AddEventListenerOptions): void {
    const handler = fn as EventListener;
    target.addEventListener(type, handler, options);
    this.cleanups.push(() => target.removeEventListener(type, handler, options));
  }

  /** Viewer coordinates → normalised over the remote frame. */
  private point(e: { clientX: number; clientY: number }): { x: number; y: number; inside: boolean } {
    const box = this.stage.getBoundingClientRect();
    const frame = letterbox(box.width, box.height, this.video.videoWidth, this.video.videoHeight);
    return toNormalised(e.clientX - box.left, e.clientY - box.top, frame);
  }

  private trace(line: string): void {
    this.o.onTrace?.(line);
  }

  private bindInput(): void {
    const enc = this.o.session.encoder;
    const stage = this.stage;
    this.listen<PointerEvent>(stage, 'pointerdown', (e) => {
      stage.focus();
      if (this.state.keysPaused) this.o.session.resumeKeys();
      if (!this.state.control) return;
      const p = this.point(e);
      if (!p.inside) return;
      e.preventDefault();
      try {
        stage.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic events */
      }
      this.trace(`md ${e.button} ${p.x.toFixed(3)},${p.y.toFixed(3)}`);
      enc.pointerDown(e.button, p.x, p.y);
    });
    this.listen<PointerEvent>(stage, 'pointermove', (e) => {
      if (!this.state.control) return;
      const p = this.point(e);
      // Outside the picture only a drag keeps reporting (clamped to the edge).
      if (!p.inside && e.buttons === 0) return;
      enc.pointerMove(p.x, p.y);
    });
    this.listen<PointerEvent>(stage, 'pointerup', (e) => {
      if (!this.state.control) return;
      const p = this.point(e);
      this.trace(`mu ${e.button} ${p.x.toFixed(3)},${p.y.toFixed(3)}`);
      enc.pointerUp(e.button, p.x, p.y);
    });
    this.listen<PointerEvent>(stage, 'pointercancel', () => enc.blur());
    this.listen<MouseEvent>(stage, 'contextmenu', (e) => {
      if (this.state.control) e.preventDefault();
    });
    this.listen<MouseEvent>(stage, 'auxclick', (e) => {
      if (this.state.control) e.preventDefault();
    });
    this.listen<WheelEvent>(
      stage,
      'wheel',
      (e) => {
        if (!this.state.control) return;
        e.preventDefault();
        enc.wheel(e.deltaX, e.deltaY, e.deltaMode);
      },
      { passive: false },
    );
    this.listen<KeyboardEvent>(stage, 'keydown', (e) => {
      if (!this.state.control) return;
      if (e.isComposing) return;
      const result = enc.keyDown(e.code, e.key, modifiers(e));
      if (result === 'release') {
        e.preventDefault();
        this.o.session.keysReleased();
        this.trace('release combo');
        return;
      }
      if (result === 'sent') {
        e.preventDefault();
        e.stopPropagation();
        this.trace(`kd ${e.code}`);
      }
    });
    this.listen<KeyboardEvent>(stage, 'keyup', (e) => {
      if (!this.state.control) return;
      enc.keyUp(e.code, e.key, modifiers(e));
      e.preventDefault();
    });
    this.listen(stage, 'blur', () => enc.blur());
    this.listen(window, 'blur', () => enc.blur());
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await this.wrap.requestFullscreen();
    } catch {
      /* refused (no user activation / not allowed in this frame) */
    }
    this.stage.focus();
  }

  private onFullscreenChange(): void {
    const full = document.fullscreenElement === this.wrap;
    this.fullButton.textContent = full ? this.o.text('web.rs.exitFullscreen') : this.o.text('web.rs.fullscreen');
    const keyboard = (navigator as Navigator & { keyboard?: { lock?: () => Promise<void>; unlock?: () => void } }).keyboard;
    if (full) void keyboard?.lock?.().catch(() => undefined);
    else keyboard?.unlock?.();
  }

  private showToast(message: string, ms: number): void {
    this.toast.textContent = message;
    this.toast.hidden = false;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = ms ? setTimeout(() => this.hideToast(), ms) : null;
  }

  private hideToast(): void {
    this.toast.hidden = true;
    this.toast.textContent = '';
  }
}
